import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { subscribeTryOnSession } from "../utils/firestoreUtils";
import "./QRDetailPage.css";

const DRAG_THRESHOLD = 100;
const MAX_DRAG = 400;

/**
 * QRDetailPage - Destination page for scanned QR codes
 * Stacked-card UI: top card is interactive (drag to advance), next card
 * renders real content behind it, the one after that is a skeleton
 * placeholder so we don't pay to render cards the user can't see.
 */
function QRDetailPage() {
  const { sessionId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dragStart, setDragStart] = useState(null);
  const [dragDelta, setDragDelta] = useState(0);

  // Preload every result image the moment a URL first shows up in Firestore
  // so swapping between cards doesn't trigger a network fetch or decode
  // flicker. Dedupe via ref so we don't re-preload on every session update.
  const preloadedUrlsRef = useRef(new Set());
  useEffect(() => {
    const urls = (sessionData?.results || [])
      .map((r) => r?.tryOnImagePublicUrl)
      .filter(Boolean);
    urls.forEach((url) => {
      if (preloadedUrlsRef.current.has(url)) return;
      preloadedUrlsRef.current.add(url);
      const img = new Image();
      img.src = url;
    });
  }, [sessionData]);

  useEffect(() => {
    if (!sessionId) {
      setError("Invalid session ID");
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeTryOnSession(sessionId, (data, err) => {
      if (err) {
        setError("Session not found or has expired");
        setLoading(false);
        return;
      }
      if (!data) {
        setError("Session not found");
        setLoading(false);
        return;
      }
      setSessionData(data);
      setLoading(false);
      if (data.results?.some((r) => r.tryOnImagePublicUrl)) {
        setError(null);
      }
    });

    return () => unsubscribe();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="qr-page-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qr-page-container">
        <div className="error-content">
          <h2>{error}</h2>
          <p>This session may have expired or been removed.</p>
        </div>
      </div>
    );
  }

  // Keep slots in fixed order — pending ones show a loading state. This
  // prevents items from reshuffling as the backend streams results in a
  // non-deterministic order.
  const displayResults = Array.isArray(sessionData?.results)
    ? sessionData.results.filter((r) => r && r.status !== "failed")
    : [];

  if (displayResults.length === 0) {
    return (
      <div className="qr-page-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <h2>Your try-ons are being generated</h2>
          <p>This page will update automatically when ready.</p>
        </div>
      </div>
    );
  }

  const N = displayResults.length;
  const safeIndex = ((selectedIndex % N) + N) % N;
  const canCycle = N > 1;

  // Wrapped offset: treats the deck as a loop. For a card at `idx` relative
  // to `safeIndex`, returns how many slots ahead it is (0 = top, 1 = peek,
  // 2 = skeleton, etc) or a small negative number if it just flew off.
  // We bias toward "ahead" so cards queue up on the right side of the stack.
  const wrappedOffset = (idx) => {
    const forward = ((idx - safeIndex) % N + N) % N; // 0..N-1
    // If the card is far ahead (more than 1 slot behind wrapping), treat it
    // as "just flew off" (-1) so the animation goes left.
    return forward === N - 1 ? -1 : forward;
  };

  // Advance the index with wraparound. Every card's transform recomputes
  // based on its new wrapped offset, and the .qr-stack-card CSS transition
  // animates them all in sync.
  const commitTransition = (direction) => {
    setDragStart(null);
    setDragDelta(0);
    setSelectedIndex((i) => {
      const next = direction < 0 ? i + 1 : i - 1;
      return ((next % N) + N) % N;
    });
  };

  const handlePrev = () => {
    if (canCycle) commitTransition(1);
  };

  const handleNext = () => {
    if (canCycle) commitTransition(-1);
  };

  const handleDragStart = (clientX) => {
    setDragStart(clientX);
    setDragDelta(0);
  };

  const handleDragMove = (clientX) => {
    if (dragStart === null) return;
    const delta = clientX - dragStart;
    setDragDelta(Math.max(Math.min(delta, MAX_DRAG), -MAX_DRAG));
  };

  const handleDragEnd = () => {
    if (dragStart === null) return;
    if (dragDelta < -DRAG_THRESHOLD && canCycle) {
      commitTransition(-1);
      return;
    }
    if (dragDelta > DRAG_THRESHOLD && canCycle) {
      commitTransition(1);
      return;
    }
    setDragStart(null);
    setDragDelta(0);
  };

  const handleTouchStart = (e) => handleDragStart(e.touches[0].clientX);
  const handleTouchMove = (e) => handleDragMove(e.touches[0].clientX);
  const handleTouchEnd = () => handleDragEnd();
  const handleMouseDown = (e) => handleDragStart(e.clientX);
  const handleMouseMove = (e) => {
    if (dragStart !== null) handleDragMove(e.clientX);
  };
  const handleMouseUp = () => handleDragEnd();
  const handleMouseLeave = () => {
    if (dragStart !== null) handleDragEnd();
  };

  // Rotation for the top card during drag, for deck feel.
  const rotation = (dragDelta / MAX_DRAG) * 8;
  const overlayOpacity = Math.max(0, 1 - Math.abs(dragDelta) / DRAG_THRESHOLD);
  const topOverlayStyle = {
    opacity: overlayOpacity,
    transition: dragStart === null ? "opacity 0.35s ease" : "none",
  };

  // Compute a style for each result based on its offset from the selected
  // card. Cards behind the top get smaller/pushed right so the user can see
  // the stack. Cards already past (offset < 0) get hidden — the outgoing one
  // is drawn as a ghost overlay instead so it can animate off-screen cleanly.
  const styleForOffset = (offset) => {
    if (offset === 0) return { transform: "translate(0, 0) scale(1)", zIndex: 30, opacity: 1 };
    if (offset === 1) return { transform: "translate(7%, 0) scale(0.93)", zIndex: 20, opacity: 1 };
    if (offset === 2) return { transform: "translate(13%, 0) scale(0.86)", zIndex: 10, opacity: 1 };
    if (offset >= 3) return { transform: "translate(18%, 0) scale(0.8)", zIndex: 5, opacity: 0 };
    // offset < 0: already past — fling off to the left
    return {
      transform: "translate(-140%, 0) scale(0.9) rotate(-15deg)",
      zIndex: 1,
      opacity: 0,
      pointerEvents: "none",
    };
  };

  return (
    <div className="qr-page-container">
      <div className="qr-page-header">
        <h1>Your Virtual Try-Ons</h1>
        <p className="qr-page-subtitle">Swipe to see the next preview</p>
      </div>

      <div className="qr-carousel-wrapper">
        <div
          className="qr-stack-viewport"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {displayResults.map((result, idx) => {
            const offset = wrappedOffset(idx);
            const baseStyle = styleForOffset(offset);
            const isTop = offset === 0;

            // While dragging the top card, follow the finger/cursor and
            // disable the CSS transition so it tracks in real time.
            let cardStyle = baseStyle;
            if (isTop && dragStart !== null) {
              cardStyle = {
                ...baseStyle,
                transform: `translateX(${dragDelta}px) rotate(${rotation}deg)`,
                transition: "none",
              };
            } else if (offset < 0) {
              // Cards already past don't animate their teleport to the back
              // of the stack on later swipes — they stay off-screen silently.
              cardStyle = { ...baseStyle, transition: "none" };
            }

            return (
              <div
                key={result.productId}
                className="qr-stack-card"
                style={cardStyle}
                aria-hidden={!isTop}
              >
                <div className="qr-media-section">
                  {isTop && result.videoPublicUrl ? (
                    <video
                      src={result.videoPublicUrl}
                      className="qr-result-video"
                      controls
                      loop
                      playsInline
                      autoPlay
                      muted
                      poster={result.tryOnImagePublicUrl}
                    />
                  ) : result.tryOnImagePublicUrl ? (
                    <>
                      <img
                        src={result.tryOnImagePublicUrl}
                        alt={result.productName}
                        className="qr-result-image"
                        draggable={false}
                      />
                      {isTop && result.status === "processing_video" && (
                        <div className="qr-processing-badge">
                          <div className="spinner-qr"></div>
                          <p>Generating video...</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="qr-pending-fill">
                      <div className="spinner"></div>
                      <p className="qr-pending-text">LOADING</p>
                    </div>
                  )}
                  <div
                    className="qr-info-overlay"
                    style={isTop ? topOverlayStyle : undefined}
                  >
                    <h3>{result.productName}</h3>
                    <p className="product-size">Size: {result.size}</p>
                    <p className="product-price">{result.price}</p>
                  </div>
                </div>
              </div>
            );
          })}

        </div>

        {canCycle && (
          <>
            <button
              className="qr-nav-arrow qr-nav-left"
              onClick={handlePrev}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className="qr-nav-arrow qr-nav-right"
              onClick={handleNext}
              aria-label="Next"
            >
              ›
            </button>
          </>
        )}
      </div>

      <div className="qr-actions">
        <a
          href="https://hifilabs.co"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-buy-now"
        >
          Buy Now
        </a>
      </div>

      {displayResults.length > 1 && (
        <div className="qr-page-indicator">
          {displayResults.map((_, index) => (
            <span
              key={index}
              className={`qr-dot ${index === safeIndex ? "active" : ""}`}
              onClick={() => setSelectedIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default QRDetailPage;
